// WHAT: Page component for detailed statistics overview
// WHY:  Provides interface for viewing comprehensive Miracle Flow statistics
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: StatsOverview component, React Router navigation
// RISKS:
//   - NOTE: Navigation path is hardcoded - may break if route changes

import { ArrowLeft, BarChart3 } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { Card, Button, Stack, Heading } from "@/components/ui";

import { StatsOverview } from "../components/organisms/StatsOverview";

/**
 * WHAT:
 *   Page component that displays detailed statistics overview for Miracle Flow
 *   sessions with comprehensive analytics.
 *
 * WHY:
 *   Enables users to view detailed insights and analytics about their Miracle Flow
 *   practice.
 *
 * BEHAVIOR:
 *   - Displays header with navigation
 *   - Renders StatsOverview component
 *   - Provides back navigation to dashboard
 *
 * RISKS:
 *   - NOTE: Navigation path is hardcoded - may break if route changes
 *
 * @returns MiracleFlowStatsOverviewTab component
 *
 */
export const MiracleFlowStatsOverviewTab: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack align="center" direction="row" justify="between">
          <Stack align="center" direction="row" gap={4}>
            <Button
              className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
              onClick={() => {
                const result = navigate("/miracle-flow");
                if (result instanceof Promise) {
                  result.catch(() => {
                    // Navigation errors are handled silently
                  });
                }
              }}
              size="sm"
              variant="outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <Stack align="center" direction="row" gap={3}>
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <Stack direction="col" gap={1}>
                <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
                  Statistics Overview
                </Heading>
                <p className="text-[#94A3B8]">Detailed analytics and insights</p>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Card>

      {/* Stats Overview Component */}
      <StatsOverview
        onBack={() => {
          const result = navigate("/miracle-flow");
          if (result instanceof Promise) {
            result.catch(() => {
              // Navigation errors are handled silently
            });
          }
        }}
      />
    </div>
  );
};
